import { Caption1, Divider, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { QuestionCircle16Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { useDataTableStyles } from "@/components/data-table";
import type { RaciLetter } from "@/engine/types";
import { ScreenHelpDrawer } from "@/features/help/ScreenHelpDrawer";
import { RACI_LABEL, RACI_LETTERS, RACI_MEANING } from "@/features/pm/raci";

const useStyles = makeStyles({
  btn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  sheet: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  block: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  text: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  wrap: {
    "&&": {
      height: "auto",
      whiteSpace: "normal",
      verticalAlign: "top",
    },
  },
  example: {
    display: "block",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "normal",
  },
  label: {
    "&&": {
      height: "auto",
      whiteSpace: "nowrap",
      verticalAlign: "top",
      width: "1%",
    },
  },
});

const LEVELS = [
  ["높음", ["밀착 관리(Manage Closely)", "양산 승인을 내는 공장장"], ["만족 유지(Keep Satisfied)", "법무와 인허가"]],
  ["낮음", ["정보 공유(Keep Informed)", "현장과 사용자"], ["모니터링(Monitor)", "자재 협력사"]],
] as const;

const SCENARIOS = [
  ["기획·타당성", "공장장은 A로 착수를 승인하고, 수행 PM은 R로 요구를 정리하고, 안전은 C로 조건을 검토하고, 고객은 I로 착수 결정을 받습니다."],
  ["라인 구축", "설치 협력사는 R로 라인을 세우고, 수행 PM은 A로 인수를 맡으며, 안전은 C로 작업 허가를 검토하고, 공장장은 I로 완료를 받습니다."],
  ["시운전·양산 이관", "품질은 R로 검사를 수행하고, 공장장은 A로 양산 시작을 승인하고, 현장은 C로 운전 조건을 확인하고, 스폰서는 I로 이관 결과를 받습니다."],
] as const;

const PMI: Record<RaciLetter, string> = {
  R: "Responsible",
  A: "Accountable",
  C: "Consulted",
  I: "Informed",
};

/** 제목 줄의 이해관계자 도움. 목업 사례 메뉴와 따로 둔다. */
export function StakeholderHelp() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={styles.btn} aria-label="도움말" title="도움말" onClick={() => setOpen(true)}>
        <QuestionCircle16Regular />
      </button>
      <ScreenHelpDrawer open={open} title="설명" onClose={() => setOpen(false)}>
        <div className={styles.sheet}>
          <section className={styles.block}>
            <h2 className={styles.text}>개요</h2>
            <p className={styles.text}>
              이해관계자(Stakeholder)는 이 플랜의 결정이나 결과에 영향을 주거나, 그 영향을 받는 사람과 조직입니다. 고객, 스폰서, 수행 PM, 협력사가 해당합니다.
            </p>
          </section>
          <Divider />
          <section className={styles.block}>
            <h2 className={styles.text}>관심과 영향</h2>
            <p className={styles.text}>
              관심(Interest)은 결과를 얼마나 지키는지이고, 영향(Influence)은 결정을 얼마나 바꿀 수 있는지입니다. 값은 낮음, 보통, 높음입니다. 네 칸은 높음과 낮음이 만나는 대응이고, 보통은 그 사이입니다.
            </p>
            <div className={table.frame}>
              <table className={table.table} aria-label="관심과 영향">
                <thead>
                  <tr>
                    <th className={table.th}>영향</th>
                    <th className={table.th}>관심 높음</th>
                    <th className={table.th}>관심 낮음</th>
                  </tr>
                </thead>
                <tbody>
                  {LEVELS.map(([level, high, low]) => (
                    <tr key={level}>
                      <td className={mergeClasses(table.td, styles.wrap)}>{level}</td>
                      <td className={mergeClasses(table.td, styles.wrap)}>
                        <span className={table.twoLine}>
                          <span>{high[0]}</span>
                          <Caption1 className={styles.example}>예: {high[1]}</Caption1>
                        </span>
                      </td>
                      <td className={mergeClasses(table.td, styles.wrap)}>
                        <span className={table.twoLine}>
                          <span>{low[0]}</span>
                          <Caption1 className={styles.example}>예: {low[1]}</Caption1>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <Divider />
          <section className={styles.block}>
            <h2 className={styles.text}>WBS</h2>
            <p className={styles.text}>행은 단계와, 산출물이 있는 작업입니다. 칸은 글자와 이름이고, 누르면 값을 고릅니다.</p>
            <div className={table.frame}>
              <table className={table.table} aria-label="RACI">
                <thead>
                  <tr>
                    <th className={table.th}>칸</th>
                    <th className={table.th}>PMI</th>
                    <th className={table.th}>뜻</th>
                  </tr>
                </thead>
                <tbody>
                  {RACI_LETTERS.map((letter) => (
                    <tr key={letter}>
                      <td className={table.td}>{letter} {RACI_LABEL[letter]}</td>
                      <td className={table.td}>{PMI[letter]}</td>
                      <td className={mergeClasses(table.td, styles.wrap)}>{RACI_MEANING[letter]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <Divider />
          <section className={styles.block}>
            <h2 className={styles.text}>예시</h2>
            <div className={table.frame}>
              <table className={table.table} aria-label="예시">
                <thead>
                  <tr>
                    <th className={table.th}>작업</th>
                    <th className={table.th}>역할</th>
                  </tr>
                </thead>
                <tbody>
                  {SCENARIOS.map(([work, roles]) => (
                    <tr key={work}>
                      <td className={mergeClasses(table.td, styles.label)}>{work}</td>
                      <td className={mergeClasses(table.td, styles.wrap)}>{roles}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </ScreenHelpDrawer>
    </>
  );
}
